/**
 * Material Cost Bridge (Phase B)
 * ------------------------------
 * READ-ONLY resolution of a job's material cost from existing procurement
 * sources (VendorPOContext + WarehouseInventoryContext) with a strict
 * lifecycle precedence:
 *
 *   1. realized   — every PO line on the job is fully received with no
 *                   unresolved discrepancy / allocation block
 *   2. committed  — ≥1 PO exists but receipt is incomplete (or partial)
 *   3. estimated  — no POs; fall back to job.financials.estimatedMaterialCost
 *   4. missing    — no procurement signal and no estimate
 *
 * This bridge does NOT:
 *   - write anything
 *   - reconcile invoices vs PO totals (no invoice model yet)
 *   - allocate overhead, freight, or restocking fees
 *   - second-guess receipt truth (it trusts ReceivingRecord state)
 */
import type { Job } from "@/data/types";
import type { PurchaseOrder } from "@/data/VendorPOContext";
import type { ReceivingRecord } from "@/data/WarehouseInventoryContext";

export type MaterialCoverage =
  | "realized"
  | "committed"
  | "partial"
  | "estimated"
  | "missing";

export interface JobRealizedMaterialCost {
  /** Resolved dollar amount; null when coverage === "missing". */
  amount: number | null;
  coverage: MaterialCoverage;
  /** Where the dollars came from. */
  source: "vendor_po" | "job_financials" | "none";
  /** Total $ across all PO lines (regardless of receipt). */
  poTotal: number;
  /** Total $ of PO lines that have been fully received (no block). */
  receivedTotal: number;
  /** Total $ of PO lines that have any discrepancy or allocation block. */
  blockedTotal: number;
  poCount: number;
  poLineCount: number;
  receivedLineCount: number;
  blockedLineCount: number;
  /** Human-friendly summary suitable for a UI sub-note. */
  notes?: string;
}

export interface MaterialCostInputs {
  job: Job;
  purchaseOrders?: PurchaseOrder[];
  receivingRecords?: ReceivingRecord[];
}

export function getJobRealizedMaterialCost(
  input: MaterialCostInputs,
): JobRealizedMaterialCost {
  const { job, purchaseOrders = [], receivingRecords = [] } = input;
  const jobPOs = purchaseOrders.filter((po) => po.jobId === job.id);
  const jobReceipts = receivingRecords.filter((r) => r.jobId === job.id);
  const estimate = job.financials?.estimatedMaterialCost ?? 0;

  if (jobPOs.length === 0) {
    if (estimate > 0) {
      return {
        amount: estimate,
        coverage: "estimated",
        source: "job_financials",
        poTotal: 0,
        receivedTotal: 0,
        blockedTotal: 0,
        poCount: 0,
        poLineCount: 0,
        receivedLineCount: 0,
        blockedLineCount: 0,
        notes: "Estimate from contract — no POs issued",
      };
    }
    return {
      amount: null,
      coverage: "missing",
      source: "none",
      poTotal: 0,
      receivedTotal: 0,
      blockedTotal: 0,
      poCount: 0,
      poLineCount: 0,
      receivedLineCount: 0,
      blockedLineCount: 0,
    };
  }

  // ── Aggregate PO + receipt state ──
  let poTotal = 0;
  let receivedTotal = 0;
  let blockedTotal = 0;
  let poLineCount = 0;
  let receivedLineCount = 0;
  let blockedLineCount = 0;

  for (const po of jobPOs) {
    for (const line of po.items) {
      poLineCount += 1;
      poTotal += line.lineTotal;

      // Find receiving rows for this PO + SKU
      const lineReceipts = jobReceipts.filter(
        (r) => r.poId === po.poId && r.sku === line.sku,
      );
      const receivedQty = lineReceipts.reduce(
        (s, r) => s + (r.receivedQuantity || 0),
        0,
      );
      const blocked = lineReceipts.some(
        (r) => r.allocationBlocked || r.discrepancy,
      );
      const fullyReceived = receivedQty >= line.quantity && line.quantity > 0;

      if (blocked) {
        blockedLineCount += 1;
        blockedTotal += line.lineTotal;
      } else if (fullyReceived) {
        receivedLineCount += 1;
        receivedTotal += line.lineTotal;
      }
    }
  }

  const allFullyReceived =
    poLineCount > 0 &&
    receivedLineCount === poLineCount &&
    blockedLineCount === 0;

  if (allFullyReceived) {
    return {
      amount: poTotal,
      coverage: "realized",
      source: "vendor_po",
      poTotal,
      receivedTotal,
      blockedTotal,
      poCount: jobPOs.length,
      poLineCount,
      receivedLineCount,
      blockedLineCount,
      notes: `All ${poLineCount} PO lines received`,
    };
  }

  if (receivedLineCount > 0 || blockedLineCount > 0) {
    return {
      amount: poTotal,
      coverage: "partial",
      source: "vendor_po",
      poTotal,
      receivedTotal,
      blockedTotal,
      poCount: jobPOs.length,
      poLineCount,
      receivedLineCount,
      blockedLineCount,
      notes:
        `${receivedLineCount} of ${poLineCount} PO lines received` +
        (blockedLineCount > 0 ? ` · ${blockedLineCount} blocked` : ""),
    };
  }

  return {
    amount: poTotal,
    coverage: "committed",
    source: "vendor_po",
    poTotal,
    receivedTotal: 0,
    blockedTotal,
    poCount: jobPOs.length,
    poLineCount,
    receivedLineCount,
    blockedLineCount,
    notes: `PO issued (${jobPOs.length}) — awaiting receipt`,
  };
}
